import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  policyKeys, SCOPE_LABELS, updatePolicyMetadata,
  type MetadataInput, type Policy, type PolicyOptions,
} from '@/lib/policies';
import { KNOWN_POLICY_TYPES } from '@shared/policy-core';

const schema = z.object({
  description: z.string(),
  policyType: z.string().trim().min(1, 'Policy type is required'),
  isRequired: z.boolean(),
  linkedId: z.string(),
});

type DetailsValues = z.infer<typeof schema>;

/** Non-versioned fields of a published policy. Title and content change by publishing a version. */
export default function PolicyDetailsForm({ policy, options }: { policy: Policy; options: PolicyOptions }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const linkField = policy.policyScope === 'feature_key' ? 'featureKeyId' : policy.policyScope === 'feature_group' ? 'featureGroupId' : null;
  const linkedId = linkField ? policy[linkField] ?? '' : '';
  const linkOptions = policy.policyScope === 'feature_key'
    ? options.keys.map(k => ({ id: k.id, name: k.name }))
    : options.groups;
  const linkValid = !linkField || linkOptions.some(o => o.id === linkedId);

  const form = useForm<DetailsValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      description: policy.description ?? '',
      policyType: policy.policyType ?? '',
      isRequired: policy.isRequired ?? true,
      linkedId,
    },
  });

  const save = useMutation({
    mutationFn: (values: DetailsValues) => {
      const input: MetadataInput = {};
      if (values.description !== (policy.description ?? '')) input.description = values.description;
      if (values.policyType.trim() !== policy.policyType) input.policyType = values.policyType.trim();
      if (values.isRequired !== policy.isRequired) input.isRequired = values.isRequired;
      if (linkField && values.linkedId && values.linkedId !== linkedId) input[linkField] = values.linkedId;
      return updatePolicyMetadata(policy.id, input);
    },
    onSuccess: detail => {
      queryClient.setQueryData(policyKeys.detail(policy.id), detail);
      queryClient.invalidateQueries({ queryKey: policyKeys.all, exact: true });
      toast({ title: 'Policy details saved' });
      form.reset(form.getValues());
    },
    onError: (error: Error) => toast({ title: 'Could not save', description: error.message, variant: 'destructive' }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Details</CardTitle>
        <CardDescription>
          These apply immediately, without a new version. To change the title or wording, publish a new version.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(values => save.mutate(values))} className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <p className="text-sm font-medium">Scope</p>
              <p className="text-sm text-slate-600">
                {SCOPE_LABELS[policy.policyScope]}
                {policy.scopeInferred && ' (inferred: the document has no policyScope; it is written on the next publish)'}
              </p>
            </div>

            {linkField ? (
              <FormField control={form.control} name="linkedId" render={({ field }) => (
                <FormItem>
                  <FormLabel>{policy.policyScope === 'feature_key' ? 'Feature' : 'Feature group'}</FormLabel>
                  <Select onValueChange={field.onChange} value={linkOptions.some(o => o.id === field.value) ? field.value : undefined}>
                    <FormControl>
                      <SelectTrigger><SelectValue placeholder={linkValid ? 'Choose' : `"${linkedId}" no longer exists, choose a replacement`} /></SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {linkOptions.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormDescription>Which part of the app asks users to accept this policy.</FormDescription>
                  <FormMessage />
                </FormItem>
              )} />
            ) : <div className="hidden md:block" />}

            <FormField control={form.control} name="policyType" render={({ field }) => (
              <FormItem>
                <FormLabel>Policy type</FormLabel>
                <FormControl><Input {...field} list="policy-type-options-details" className="font-mono" /></FormControl>
                <datalist id="policy-type-options-details">
                  {KNOWN_POLICY_TYPES.map(t => <option key={t} value={t} />)}
                </datalist>
                <FormMessage />
              </FormItem>
            )} />

            <FormField control={form.control} name="isRequired" render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <FormLabel>Acceptance required</FormLabel>
                  <FormDescription>Turning this on asks every user who has not accepted to do so.</FormDescription>
                </div>
                <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
              </FormItem>
            )} />

            <FormField control={form.control} name="description" render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>Description</FormLabel>
                <FormControl><Textarea rows={3} {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <div className="md:col-span-2 flex justify-end">
              <Button type="submit" disabled={save.isPending || !form.formState.isDirty}>
                {save.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save details
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
